import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './MiPedido.css';
import baseURL from '../url';
import moneda from '../moneda';

const normalizeIdPedido = value => String(value ?? '').trim();
const normalizeTokenConsulta = value => String(value ?? '').trim();

const createCredentialPair = (idPedido, tokenConsulta) => {
    const normalizedId = normalizeIdPedido(idPedido);
    const normalizedToken = normalizeTokenConsulta(tokenConsulta);

    if (!/^[1-9]\d*$/.test(normalizedId) || !normalizedToken) {
        return null;
    }

    return {
        idPedido: normalizedId,
        tokenConsulta: normalizedToken,
    };
};

const sameCredentialPair = (firstPair, secondPair) => Boolean(
    firstPair
    && secondPair
    && firstPair.idPedido === secondPair.idPedido
    && firstPair.tokenConsulta === secondPair.tokenConsulta
);

const readStoredCredentialPair = () => {
    try {
        const storedId = localStorage.getItem('idPedido');
        const storedToken = localStorage.getItem('tokenConsulta');

        return {
            hasAny: Boolean(storedId || storedToken),
            pair: createCredentialPair(storedId, storedToken),
        };
    } catch (error) {
        return { hasAny: false, pair: null };
    }
};

const parseOrderProducts = (pedido) => {
    if (Array.isArray(pedido?.productos)) {
        return pedido.productos;
    }

    if (typeof pedido?.productos !== 'string') {
        return [];
    }

    try {
        const parsedProducts = JSON.parse(pedido.productos);
        return Array.isArray(parsedProducts) ? parsedProducts : [];
    } catch (error) {
        return [];
    }
};

const formatOrderDate = (value) => {
    if (!value) {
        return 'Fecha no disponible';
    }

    const date = new Date(
        typeof value === 'string' ? value.replace(' ', 'T') : value
    );

    if (Number.isNaN(date.getTime())) {
        return 'Fecha no disponible';
    }

    return date.toLocaleString('es-CO', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatOrderMoney = (value, fallback = 'Valor no disponible') => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue)
        ? `${moneda} ${numericValue.toLocaleString('es-CO')}`
        : fallback;
};

const hasNumericValue = value => (
    value !== null
    && value !== undefined
    && value !== ''
    && Number.isFinite(Number(value))
);

export default function MiPedido() {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchKey = searchParams.toString();
    const [idPedido, setIdPedido] = useState('');
    const [tokenConsulta, setTokenConsulta] = useState('');
    const [pedidoDetalle, setPedidoDetalle] = useState(null);
    const [lookupState, setLookupState] = useState('initial');
    const requestSequenceRef = useRef(0);
    const requestControllerRef = useRef(null);
    const lastAutomaticPairRef = useRef(null);
    const lastAttemptedPairRef = useRef(null);

    const cancelCurrentRequest = useCallback(() => {
        requestSequenceRef.current += 1;
        requestControllerRef.current?.abort();
        requestControllerRef.current = null;
    }, []);

    const consultarPedido = useCallback(async (credentialPair) => {
        if (!credentialPair) {
            cancelCurrentRequest();
            setPedidoDetalle(null);
            setLookupState('incomplete');
            return;
        }

        cancelCurrentRequest();
        lastAttemptedPairRef.current = credentialPair;
        setPedidoDetalle(null);

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setLookupState('offline');
            return;
        }

        const requestId = requestSequenceRef.current;
        const controller = typeof AbortController === 'function'
            ? new AbortController()
            : null;
        requestControllerRef.current = controller;
        setLookupState('loading');

        try {
            const queryParams = new URLSearchParams({
                idPedido: credentialPair.idPedido,
                tokenConsulta: credentialPair.tokenConsulta,
            });
            const response = await fetch(
                `${baseURL}/pedidoPublicGet.php?${queryParams.toString()}`,
                {
                    method: 'GET',
                    ...(controller ? { signal: controller.signal } : {}),
                }
            );

            let data = null;
            try {
                data = await response.json();
            } catch (error) {
                data = null;
            }

            if (requestSequenceRef.current !== requestId) {
                return;
            }

            if (!response.ok || !data?.pedido) {
                setPedidoDetalle(null);
                setLookupState('not-found');
                return;
            }

            setPedidoDetalle(data.pedido);
            setLookupState('success');

            try {
                localStorage.setItem('idPedido', credentialPair.idPedido);
                localStorage.setItem('tokenConsulta', credentialPair.tokenConsulta);
            } catch (error) {
                // La consulta sigue siendo válida aunque el almacenamiento no esté disponible.
            }
        } catch (error) {
            if (
                requestSequenceRef.current !== requestId
                || error?.name === 'AbortError'
            ) {
                return;
            }

            setPedidoDetalle(null);
            setLookupState(
                typeof navigator !== 'undefined' && navigator.onLine === false
                    ? 'offline'
                    : 'network-error'
            );
        } finally {
            if (
                requestSequenceRef.current === requestId
                && requestControllerRef.current === controller
            ) {
                requestControllerRef.current = null;
            }
        }
    }, [cancelCurrentRequest]);

    useEffect(() => {
        const urlHasId = searchParams.has('idPedido');
        const urlHasToken = searchParams.has('tokenConsulta');
        const urlHasAnyCredential = urlHasId || urlHasToken;
        const urlId = searchParams.get('idPedido');
        const urlToken = searchParams.get('tokenConsulta');
        const urlPair = createCredentialPair(urlId, urlToken);

        let automaticPair = null;
        let incompleteCredentials = false;

        if (urlHasAnyCredential) {
            automaticPair = urlPair;
            incompleteCredentials = !urlPair;
            setIdPedido(normalizeIdPedido(urlId));
            setTokenConsulta(normalizeTokenConsulta(urlToken));
        } else {
            const storedCredentials = readStoredCredentialPair();
            automaticPair = storedCredentials.pair;
            incompleteCredentials = storedCredentials.hasAny && !storedCredentials.pair;

            if (automaticPair) {
                setIdPedido(automaticPair.idPedido);
                setTokenConsulta(automaticPair.tokenConsulta);
            }
        }

        if (incompleteCredentials) {
            cancelCurrentRequest();
            lastAutomaticPairRef.current = null;
            setPedidoDetalle(null);
            setLookupState('incomplete');
            return;
        }

        if (!automaticPair) {
            cancelCurrentRequest();
            lastAutomaticPairRef.current = null;
            setPedidoDetalle(null);
            setLookupState('initial');
            return;
        }

        if (sameCredentialPair(lastAutomaticPairRef.current, automaticPair)) {
            return;
        }

        lastAutomaticPairRef.current = automaticPair;
        consultarPedido(automaticPair);
    }, [
        cancelCurrentRequest,
        consultarPedido,
        searchKey,
        searchParams,
    ]);

    useEffect(() => () => {
        lastAutomaticPairRef.current = null;
        cancelCurrentRequest();
    }, [cancelCurrentRequest]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const credentialPair = createCredentialPair(idPedido, tokenConsulta);

        if (!credentialPair) {
            consultarPedido(null);
            return;
        }

        lastAutomaticPairRef.current = credentialPair;
        const nextParams = new URLSearchParams({
            idPedido: credentialPair.idPedido,
            tokenConsulta: credentialPair.tokenConsulta,
        });
        setSearchParams(nextParams);
        consultarPedido(credentialPair);
    };

    const handleRetry = () => {
        consultarPedido(lastAttemptedPairRef.current);
    };

    const handleNewLookup = () => {
        cancelCurrentRequest();
        lastAutomaticPairRef.current = null;
        lastAttemptedPairRef.current = null;
        setPedidoDetalle(null);
        setLookupState('initial');
        setIdPedido('');
        setTokenConsulta('');

        try {
            localStorage.removeItem('idPedido');
            localStorage.removeItem('tokenConsulta');
            localStorage.removeItem('consultaUrl');
        } catch (error) {
            // La vista también puede reiniciarse si el almacenamiento está bloqueado.
        }

        setSearchParams(new URLSearchParams());
    };

    const orderProducts = useMemo(
        () => parseOrderProducts(pedidoDetalle),
        [pedidoDetalle]
    );

    const hasDeliveryQuote = hasNumericValue(pedidoDetalle?.deliveryQuote);
    const isHomeDelivery = pedidoDetalle?.entregaResumen === 'Envío a domicilio';
    const subtotalForDisplay = hasNumericValue(pedidoDetalle?.subtotalProductos)
        ? pedidoDetalle.subtotalProductos
        : (!isHomeDelivery && hasNumericValue(pedidoDetalle?.total)
            ? pedidoDetalle.total
            : null);
    const deliveryForDisplay = hasDeliveryQuote
        ? formatOrderMoney(pedidoDetalle.deliveryQuote)
        : (isHomeDelivery ? 'No disponible para este pedido histórico' : null);

    return (
        <main
            className="orderLookup"
            aria-busy={lookupState === 'loading'}
        >
            <header className="orderLookup__header">
                <p>Seguimiento protegido</p>
                <h1>Mi pedido</h1>
                <p>
                    Consulta el estado con el número del pedido y el código privado
                    recibido al finalizar la compra.
                </p>
            </header>

            <form className="orderLookup__form" onSubmit={handleSubmit} noValidate>
                <div className="orderLookup__field">
                    <label htmlFor="order-lookup-id">Número de pedido</label>
                    <input
                        id="order-lookup-id"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        aria-invalid={lookupState === 'incomplete'}
                        aria-describedby={
                            lookupState === 'incomplete'
                                ? 'order-lookup-validation'
                                : undefined
                        }
                        value={idPedido}
                        onChange={event => setIdPedido(event.target.value)}
                    />
                </div>

                <div className="orderLookup__field">
                    <label htmlFor="order-lookup-token">Código de consulta</label>
                    <input
                        id="order-lookup-token"
                        type="password"
                        autoComplete="off"
                        aria-invalid={lookupState === 'incomplete'}
                        aria-describedby={
                            lookupState === 'incomplete'
                                ? 'order-lookup-validation'
                                : undefined
                        }
                        value={tokenConsulta}
                        onChange={event => setTokenConsulta(event.target.value)}
                    />
                </div>

                <button
                    type="submit"
                    className="orderLookup__primaryAction"
                    disabled={lookupState === 'loading'}
                >
                    {lookupState === 'loading' ? 'Consultando…' : 'Consultar pedido'}
                </button>
            </form>

            <div className="orderLookup__feedback" aria-live="polite">
                {lookupState === 'initial' && (
                    <p role="status">
                        Ingresa el número del pedido y su código de consulta para comenzar.
                    </p>
                )}
                {lookupState === 'incomplete' && (
                    <p id="order-lookup-validation" role="alert">
                        Completa un número de pedido válido y el código de consulta.
                    </p>
                )}
                {lookupState === 'loading' && (
                    <p role="status">Verificando el pedido con el servidor…</p>
                )}
                {lookupState === 'not-found' && (
                    <p role="alert">
                        No encontramos un pedido con esas credenciales. Revisa los datos
                        e inténtalo nuevamente.
                    </p>
                )}
                {lookupState === 'network-error' && (
                    <div role="alert">
                        <p>No pudimos conectarnos para verificar el pedido.</p>
                        <button type="button" onClick={handleRetry}>Reintentar</button>
                    </div>
                )}
                {lookupState === 'offline' && (
                    <div role="alert">
                        <p>
                            No tienes conexión. El pedido no puede verificarse ni mostrarse
                            como actualizado.
                        </p>
                        <button type="button" onClick={handleRetry}>Reintentar</button>
                    </div>
                )}
            </div>

            {lookupState === 'success' && pedidoDetalle && (
                <section className="orderLookup__result">
                    <div className="orderLookup__summary">
                        <div>
                            <p>Pedido confirmado por el servidor</p>
                            <h2>Resumen del pedido</h2>
                        </div>
                        <button
                            type="button"
                            className="orderLookup__secondaryAction"
                            onClick={handleNewLookup}
                        >
                            Consultar otro pedido
                        </button>

                        <dl>
                            {pedidoDetalle.nombre && (
                                <>
                                    <dt>Cliente</dt>
                                    <dd>{pedidoDetalle.nombre}</dd>
                                </>
                            )}
                            <dt>Estado</dt>
                            <dd>{pedidoDetalle.estado || 'Por confirmar'}</dd>
                            <dt>Número</dt>
                            <dd>{pedidoDetalle.idPedido || 'No disponible'}</dd>
                            <dt>Entrega</dt>
                            <dd>
                                {pedidoDetalle.entregaResumen
                                    || pedidoDetalle.entrega
                                    || 'Por confirmar'}
                            </dd>
                            <dt>Fecha</dt>
                            <dd>
                                {formatOrderDate(
                                    pedidoDetalle.createdAt || pedidoDetalle.fecha
                                )}
                            </dd>
                        </dl>
                    </div>

                    <div className="orderLookup__products">
                        <h2>Productos del pedido</h2>
                        {orderProducts.length === 0 ? (
                            <p role="status">Este pedido no incluye productos visibles.</p>
                        ) : (
                            <div className="orderLookup__productGrid">
                                {orderProducts.map((product, index) => {
                                    const productTitle = String(product?.titulo ?? '').trim()
                                        || 'Producto';
                                    const productKey = product?.idProducto
                                        ?? `${productTitle}-${index}`;
                                    const productItems = Array.isArray(product?.items)
                                        ? product.items.filter(Boolean)
                                        : [];

                                    return (
                                        <article
                                            className="orderLookup__product"
                                            key={productKey}
                                        >
                                            {product?.imagen ? (
                                                <img
                                                    src={product.imagen}
                                                    alt={`Imagen de ${productTitle}`}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            ) : (
                                                <div
                                                    className="orderLookup__imageFallback"
                                                    aria-hidden="true"
                                                >
                                                    Sin imagen
                                                </div>
                                            )}
                                            <div>
                                                <h3>{productTitle}</h3>
                                                {product?.categoria && (
                                                    <p>{product.categoria}</p>
                                                )}
                                                <p>
                                                    {formatOrderMoney(product?.precio)}
                                                    {' · '}
                                                    Cantidad: {product?.cantidad ?? 'No disponible'}
                                                </p>
                                                {productItems.length > 0 && (
                                                    <p>{productItems.join(', ')}</p>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="orderLookup__total">
                        <dl
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                gap: '8px 24px',
                                width: '100%',
                                margin: 0,
                                alignItems: 'baseline',
                            }}
                        >
                            <dt>Subtotal productos</dt>
                            <dd style={{ margin: 0, fontWeight: 700 }}>
                                {formatOrderMoney(
                                    subtotalForDisplay,
                                    'Subtotal no disponible'
                                )}
                            </dd>
                            {deliveryForDisplay !== null && (
                                <>
                                    <dt>Domicilio</dt>
                                    <dd style={{ margin: 0, fontWeight: 700 }}>
                                        {deliveryForDisplay}
                                    </dd>
                                </>
                            )}
                            <dt style={{ fontWeight: 700 }}>Total confirmado</dt>
                            <dd
                                style={{
                                    margin: 0,
                                    fontWeight: 700,
                                    fontSize: 'clamp(1.4rem, 4vw, 2rem)',
                                }}
                            >
                                {formatOrderMoney(
                                    pedidoDetalle.total,
                                    'Total no disponible'
                                )}
                            </dd>
                        </dl>
                    </div>
                </section>
            )}
        </main>
    );
}
